import React, { useEffect, useState } from 'react';
import { Star, ExternalLink } from 'lucide-react';
import { getReviews } from '../api/api';

const Etoiles = ({ note, taille = 16 }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={`${note} sur 5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        width={taille}
        height={taille}
        className={i <= Math.round(note) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}
        aria-hidden="true"
      />
    ))}
  </span>
);

export const GoogleReviews = () => {
  const [donnees, setDonnees] = useState(null);

  useEffect(() => {
    getReviews()
      .then(setDonnees)
      .catch(() => setDonnees(null));
  }, []);

  const avis = donnees?.items || [];

  // Rien ne s'affiche tant que les vraies valeurs de la fiche ne sont pas saisies.
  if (!donnees?.enabled || (!donnees.rating && avis.length === 0)) return null;

  return (
    <section id="avis" className="py-16 sm:py-20 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
            Ce que disent nos clients
          </h2>

          {donnees.rating && (
            <div className="mt-4 inline-flex flex-wrap items-center justify-center gap-3 bg-white border border-gray-200 rounded-full px-5 py-2.5">
              <span className="text-2xl font-bold text-gray-900">
                {donnees.rating.toFixed(1).replace('.', ',')}
              </span>
              <Etoiles note={donnees.rating} taille={18} />
              {donnees.review_count > 0 && (
                <span className="text-sm text-gray-600">
                  {donnees.review_count} avis Google
                </span>
              )}
            </div>
          )}
        </div>

        {avis.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {avis.map((a) => (
              <figure
                key={a.id}
                className="bg-white border border-gray-200 rounded-xl p-6 flex flex-col"
              >
                <Etoiles note={a.rating} />
                <blockquote className="mt-3 text-gray-700 leading-relaxed flex-1">
                  {a.text}
                </blockquote>
                <figcaption className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-3">
                  {a.photo_url && (
                    <img
                      src={a.photo_url}
                      alt=""
                      width="32"
                      height="32"
                      loading="lazy"
                      className="w-8 h-8 rounded-full shrink-0"
                    />
                  )}
                  <span className="min-w-0">
                    {a.author_url ? (
                      <a
                        href={a.author_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-gray-900 hover:text-blue-600"
                      >
                        {a.author}
                      </a>
                    ) : (
                      <span className="font-semibold text-gray-900">{a.author}</span>
                    )}
                    {a.date && <span className="text-sm text-gray-500 block">{a.date}</span>}
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        )}

        {donnees.is_filtered && (
          <p className="mt-6 text-center text-sm text-gray-500 max-w-2xl mx-auto">
            Vous consultez une sélection de nos avis. La note ci-dessus porte sur
            l'intégralité des {donnees.review_count} avis reçus.{' '}
            {donnees.profile_url && (
              <a
                href={donnees.profile_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline hover:text-blue-700"
              >
                Consultez-les tous sur notre fiche Google
              </a>
            )}
          </p>
        )}

        <div className="mt-10 flex flex-wrap justify-center gap-3">
          {donnees.profile_url && (
            <a
              href={donnees.profile_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border-2 border-gray-200 hover:border-blue-600 px-5 py-2.5 rounded-lg text-sm font-semibold text-gray-900 transition-colors"
            >
              Voir tous les avis
              <ExternalLink className="w-4 h-4" aria-hidden="true" />
            </a>
          )}
          {donnees.review_link && (
            <a
              href={donnees.review_link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors"
            >
              Laisser un avis
            </a>
          )}
        </div>
      </div>
    </section>
  );
};

export default GoogleReviews;
